import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { FulfillmentStage, OrderItemComponentView, OrderItemView, OrderView, PreparationItemView, PreparationState } from '../../shared/types';
import { Badge, Button, Card, EmptyState, HeroSelectField, LoadingScreen, PageHeader, cx } from '../components/Ui';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useBusinessBranches, useKitchenOrders, useKitchenZones } from '../hooks/businessData';
import { useSse } from '../hooks/useSse';
import { apiRequest, jsonBody } from '../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { fullscreenApiSupported, fullscreenButtonLabel, isKioskSearch, kioskKitchenPath, KIOSK_ENTER_LABEL, KIOSK_EXIT_LABEL, normalKitchenPath } from '../lib/kitchenKiosk';

const prepLabels: Record<PreparationState, string> = {
  pendiente: 'Pendiente',
  preparacion: 'Preparando',
  completado: 'Listo',
};

const fulfillmentLabels: Record<FulfillmentStage, string> = {
  kitchen: 'En cocina',
  delivery_prep: 'En preparación (reparto)',
  ready: 'Listo para entregar o despachar',
  transit: 'En reparto',
  delivered: 'Entregado',
  handed_off: 'Retirado o entregado en local',
  cancelled: 'Cancelado',
};

function formatDuration(ms: number | null): string {
  if (ms === null) return 'Sin evidencia';
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `${hours} h ${remaining} min` : `${hours} h`;
}

interface TaskConfiguration {
  orderItem: OrderItemView | null;
  component: OrderItemComponentView | null;
}

function configurationForTask(order: OrderView, task: PreparationItemView): TaskConfiguration {
  const orderItem = order.items.find((item) => item.id === task.orderItemId) ?? null;
  const component = task.componentId
    ? orderItem?.components.find((item) => item.id === task.componentId) ?? null
    : null;
  return { orderItem, component };
}

function TaskCard({ order, task, busy, onStatus, kiosk = false }: { order: OrderView; task: PreparationItemView; busy: boolean; onStatus(status: PreparationState): void; kiosk?: boolean }) {
  const canSeeDetails = task.status !== 'pendiente';
  const { orderItem, component } = configurationForTask(order, task);
  const options = component?.options ?? orderItem?.options ?? [];
  const ingredients = component?.ingredients ?? orderItem?.ingredients ?? [];
  const excluded = ingredients.filter((ingredient) => ingredient.excluded);
  const added = ingredients.filter((ingredient) => ingredient.included && ingredient.extraPriceUsdCents > 0);
  const actionButtonClass = kiosk ? 'h-14 px-8 text-lg' : undefined;
  return (
    <Card className={cx('relative overflow-hidden p-0', task.status === 'preparacion' && 'ring-2 ring-amber-400')}>
      <div className="flex items-stretch">
        <div className={cx('grid place-items-center bg-slate-950 p-3 text-center text-white', kiosk ? 'min-w-36 p-5' : 'min-w-24')}>
          <div><div className={cx('font-black uppercase tracking-[.2em] text-slate-400', kiosk ? 'text-xs' : 'text-[10px]')}>Pedido</div><div className={cx('font-black leading-none', kiosk ? 'text-6xl' : 'text-4xl')}>#{String(order.correlation).padStart(2, '0')}</div><div className={cx(kiosk ? 'mt-3 text-sm text-slate-300' : 'mt-2 text-xs text-slate-300')}>{new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div></div>
        </div>
        <div className="min-w-0 flex-1 p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className={cx('font-black', kiosk ? 'text-3xl' : 'text-xl')}>{canSeeDetails ? <><span className="mr-2 rounded-lg bg-slate-100 px-2 py-1">{task.quantity}×</span>{task.productName}</> : 'Tarea pendiente'}</h3>
              <div className={cx('mt-2 flex flex-wrap items-center gap-2 font-bold', kiosk ? 'text-sm' : 'text-xs')}>
                {canSeeDetails && task.variantName && <span className="rounded-full bg-violet-100 px-2.5 py-1 text-violet-800">Variante: {task.variantName}</span>}
                {canSeeDetails && component && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">{component.componentName} de {orderItem?.productName}</span>}
                <span className="uppercase tracking-wider text-slate-500">Zona: {task.preparationZone}</span>
              </div>
            </div>
            <Badge tone={task.status === 'completado' ? 'green' : task.status === 'preparacion' ? 'amber' : 'slate'}>{prepLabels[task.status]}</Badge>
          </div>
          <div className={cx('mt-3 grid gap-2 rounded-xl bg-slate-50 p-3 text-slate-600 sm:grid-cols-2', kiosk ? 'text-sm' : 'text-xs')} aria-label={`Estado de cumplimiento del pedido ${order.correlation}`}>
            {order.tableName && <div><span className="font-bold text-slate-900">Mesa:</span> {order.tableName} · Pedido #{order.correlation}</div>}
            {order.waiterName && <div><span className="font-bold text-slate-900">Mesero:</span> {order.waiterName}</div>}
            <div><span className="font-bold text-slate-900">Cumplimiento:</span> {order.fulfillmentStage ? fulfillmentLabels[order.fulfillmentStage] : 'Sin seguimiento'}</div>
            <div><span className="font-bold text-slate-900">Tiempo en cocina:</span> {formatDuration(order.fulfillmentTimings.kitchenMs)}</div>
            <div className="sm:col-span-2 text-[11px] leading-5 text-slate-500">Listo significa que cocina terminó; entrega, retiro o reparto se confirman fuera de cocina.</div>
          </div>
          {!canSeeDetails && <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm font-semibold text-slate-600">Iniciá la cocción para ver qué preparar.</p>}
          {canSeeDetails && options.length > 0 && <div className={cx('mt-3 rounded-xl bg-sky-50 p-3 font-semibold text-sky-950', kiosk ? 'text-base' : 'text-sm')}><strong>Opciones:</strong> {options.map((option) => `${option.groupName}: ${option.quantity > 1 ? `${option.quantity}× ` : ''}${option.optionName}`).join(' · ')}</div>}
          {canSeeDetails && excluded.length > 0 && <div className={cx('mt-3 rounded-xl bg-rose-50 p-3 font-black text-rose-800', kiosk ? 'text-base' : 'text-sm')}>SIN: {excluded.map((ingredient) => ingredient.name).join(', ')}</div>}
          {canSeeDetails && added.length > 0 && <div className={cx('mt-3 rounded-xl bg-emerald-50 p-3 font-black text-emerald-800', kiosk ? 'text-base' : 'text-sm')}>EXTRA: {added.map((ingredient) => ingredient.name).join(', ')}</div>}
          {canSeeDetails && order.notes && <p className="mt-2 rounded-xl bg-amber-50 p-2 text-sm text-amber-900"><strong>Nota:</strong> {order.notes}</p>}
          <div className="mt-4 flex flex-wrap gap-2">
            {task.status === 'pendiente' && <Button busy={busy} className={actionButtonClass} onClick={() => onStatus('preparacion')}>Comenzar</Button>}
            {task.status === 'preparacion' && <Button variant="success" busy={busy} className={actionButtonClass} onClick={() => onStatus('completado')}>✓ Marcar listo</Button>}
            {task.status === 'preparacion' && <Button variant="secondary" busy={busy} className={actionButtonClass} onClick={() => onStatus('pendiente')}>Devolver a pendiente</Button>}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function KitchenPage() {
  const { user } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [branchId, setBranchId] = useState(user?.branchId ?? '');
  const [zone, setZone] = useState('');
  const [busyItem, setBusyItem] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(() => typeof document !== 'undefined' && document.fullscreenElement != null);
  const canChooseBranch = user?.role === 'owner' || user?.role === 'manager';
  const kiosk = isKioskSearch(searchParams);
  const fullscreenSupported = useMemo(fullscreenApiSupported, []);

  const { data: allBranches = [] } = useBusinessBranches();
  const branches = useMemo(() => {
    if (!canChooseBranch && user?.branchId) {
      return [{ id: user.branchId, name: 'Sucursal asignada', active: true }];
    }
    return allBranches.filter((branch) => branch.active);
  }, [canChooseBranch, user?.branchId, allBranches]);

  useEffect(() => {
    if (!branchId && branches.length) setBranchId(branches[0].id);
  }, [branchId, branches]);

  const { data: zones = [] } = useKitchenZones(branchId);
  const { data: orders = [], isLoading } = useKitchenOrders(branchId, zone);
  const loading = isLoading && !branches.length;

  useEffect(() => {
    if (zone && zones.length > 0 && !zones.includes(zone)) setZone('');
  }, [zone, zones]);

  useEffect(() => {
    if (!fullscreenSupported) return undefined;
    const onFullscreenChange = () => setIsFullscreen(document.fullscreenElement != null);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [fullscreenSupported]);

  const invalidateOrdersRef = useRef(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
  });
  useEffect(() => {
    invalidateOrdersRef.current = () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    };
  }, [queryClient]);
  const streamStatus = useSse(branchId, () => { invalidateOrdersRef.current(); });

  const queue = useMemo(() => orders
    .flatMap((order) => order.preparationItems
      .filter((task) => task.status !== 'completado' && (!zone || task.preparationZone === zone))
      .map((task) => ({ order, task })))
    .sort((a, b) => a.order.createdAt.localeCompare(b.order.createdAt)), [orders, zone]);

  const setPreparation = async (order: OrderView, task: PreparationItemView, status: PreparationState) => {
    setBusyItem(task.id);
    try {
      await apiRequest(`/api/orders/${order.id}/items/${task.id}/preparation`, { method: 'POST', body: jsonBody({ status }) });
      show(status === 'completado' ? `Tarea del pedido #${String(order.correlation).padStart(2, '0')} lista.` : 'Estado de preparación actualizado.', 'success');
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo actualizar la tarea.', 'error');
    } finally { setBusyItem(null); }
  };

  const toggleFullscreen = async () => {
    if (typeof document === 'undefined') return;
    try {
      if (document.fullscreenElement) {
        if (typeof document.exitFullscreen === 'function') await document.exitFullscreen();
        return;
      }
      if (typeof document.documentElement.requestFullscreen === 'function') await document.documentElement.requestFullscreen();
    } catch {
      // Fullscreen API unavailable or blocked (e.g. embedded iframe); the page stays usable.
    }
  };

  const enterKiosk = () => navigate(kioskKitchenPath());
  const exitKiosk = () => navigate(normalKitchenPath(), { replace: true });

  const streamBadge = (
    <span className={cx('inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-bold', streamStatus === 'connected' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700')}><span className={cx('h-2 w-2 rounded-full', streamStatus === 'connected' ? 'bg-emerald-500' : 'bg-slate-500')} />{streamStatus === 'connected' ? 'En vivo' : 'Reconectando'}</span>
  );

  const fullscreenButton = fullscreenSupported ? (
    <Button variant="secondary" aria-pressed={isFullscreen} onClick={() => void toggleFullscreen()}>{fullscreenButtonLabel(isFullscreen)}</Button>
  ) : null;
  const kioskButton = <Button variant="secondary" onClick={enterKiosk}>{KIOSK_ENTER_LABEL}</Button>;

  if (loading) return <LoadingScreen label="Abriendo cocina…" />;

  if (kiosk) {
    return (
      <div className="app-brand-shell flex min-h-screen flex-col gap-4 p-4 lg:p-5">
        <Card className="flex flex-wrap items-end justify-between gap-4">
          {canChooseBranch ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <HeroSelectField label="Sucursal" value={branchId || null} isDisabled={!canChooseBranch} onChange={(value) => setBranchId(String(value ?? ''))} options={branches.map((branch) => ({ id: branch.id, label: branch.name }))} />
              <HeroSelectField label="Zona de preparación" value={zone || null} placeholder="Todas las zonas" onChange={(value) => setZone(String(value ?? ''))} options={zones.map((value) => ({ id: value, label: value }))} />
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-xl bg-slate-950 px-5 py-3 text-center text-white"><div className="text-xs text-slate-400">Tareas en cola</div><div className="text-3xl font-black">{queue.length}</div></div>
            {streamBadge}
            {fullscreenButton}
            <Button variant="secondary" onClick={exitKiosk}>{KIOSK_EXIT_LABEL}</Button>
          </div>
        </Card>
        {queue.length ? <div className="grid flex-1 content-start gap-5 lg:grid-cols-2 2xl:grid-cols-3">{queue.map(({ order, task }) => <TaskCard key={task.id} order={order} task={task} busy={busyItem === task.id} kiosk onStatus={(status) => void setPreparation(order, task, status)} />)}</div> : <EmptyState title="Cocina al día" description={zone ? `No hay tareas pendientes en la zona "${zone}".` : 'No hay tareas pendientes de preparación.'} />}
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Cocina · Preparación" description="Cola en tiempo real, agrupada por zona de preparación." action={<div className="flex flex-wrap items-center gap-2">{streamBadge}{kioskButton}{fullscreenButton}</div>} />
      <Card className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
        <HeroSelectField label="Sucursal" value={branchId || null} isDisabled={!canChooseBranch} onChange={(value) => setBranchId(String(value ?? ''))} options={branches.map((branch) => ({ id: branch.id, label: branch.name }))} />
        <HeroSelectField label="Zona de preparación" value={zone || null} placeholder="Todas las zonas" onChange={(value) => setZone(String(value ?? ''))} options={zones.map((value) => ({ id: value, label: value }))} />
        <div className="rounded-xl bg-slate-950 px-5 py-3 text-center text-white"><div className="text-xs text-slate-400">Tareas en cola</div><div className="text-2xl font-black">{queue.length}</div></div>
      </Card>
      {queue.length ? <div className="grid gap-4 xl:grid-cols-2">{queue.map(({ order, task }) => <TaskCard key={task.id} order={order} task={task} busy={busyItem === task.id} onStatus={(status) => void setPreparation(order, task, status)} />)}</div> : <EmptyState title="Cocina al día" description={zone ? `No hay tareas pendientes en la zona "${zone}".` : 'No hay tareas pendientes de preparación.'} />}
    </div>
  );
}
